import { useNavigate, useParams } from "react-router-dom";
import { useGoBack } from "@/hooks/use-go-back";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import {
  deleteEventType,
  getEventType,
} from "@/features/supply-config/event-types-api";
import {
  EVENT_TYPE_FLAGS,
  getEventTypeFlags,
  setEventTypeFlag,
  type EventTypeFlagType,
} from "@/features/supply-config/feature-flags-api";
import { ContentLayout } from "@/components/common/content-layout";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <div className="text-sm text-gray-900 mt-0.5 break-all">{value || "—"}</div>
    </div>
  );
}

export default function EventTypeDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useGoBack("..");
  const qc = useQueryClient();

  const { data: et, isLoading, isFetching, error } = useQuery({
    queryKey: ["event-type", id],
    queryFn: () => getEventType(id!),
    enabled: !!id,
  });

  const remove = useMutation({
    mutationFn: () => deleteEventType(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["event-types"] });
      toast({ title: "Event type deleted" });
      navigate("..");
    },
    onError: (e: any) =>
      toast({
        title: "Error",
        description: e.response?.data?.message || "Failed",
        variant: "destructive",
      }),
  });

  // Feature flags keyed by this event type's Calendly URI.
  const uri = et?.calendly_event_type_uri ?? undefined;
  const { data: flags = [] } = useQuery({
    queryKey: ["feature-flags", uri],
    queryFn: () => getEventTypeFlags(uri!),
    enabled: !!uri,
  });
  const flagByType = (t: EventTypeFlagType) => flags.find((f) => f.type === t);

  const setFlag = useMutation({
    mutationFn: (vars: { type: EventTypeFlagType; enabled: boolean }) =>
      setEventTypeFlag({
        type: vars.type,
        identifier: uri!,
        enabled: vars.enabled,
        // Free-form label; the round-robin `type` slug is a useful default.
        key: et?.type || uri!,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feature-flags", uri] });
      toast({ title: "Feature flag saved" });
    },
    onError: (e: any) =>
      toast({
        title: "Error",
        description: e.response?.data?.message || "Failed",
        variant: "destructive",
      }),
  });

  return (
    <ContentLayout
      data={et ? [et] : []}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      contentClassName="space-y-6"
      errorMessage="Failed to load event type"
      emptyState={{ message: "Event type not found" }}
    >
      {et && (
        <div className="w-full space-y-6">
          <button
            type="button"
            onClick={goBack}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" /> Event types
          </button>

          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold text-gray-900">
                {et.managed_event?.name ?? "Event type"}
              </h1>
              <div className="flex items-center gap-2 mt-2">
                {et.professional ? (
                  <Badge variant="outline">Pro · {et.professional.name}</Badge>
                ) : et.teams?.length ? (
                  <Badge variant="outline">
                    Round-robin · {et.teams.map((t) => t.name).join(", ")}
                    {et.type ? ` · ${et.type}` : et.language ? ` · ${et.language}` : ""}
                  </Badge>
                ) : null}
                <Badge
                  variant={et.is_active ? "active" : "inactive"}
                  className="uppercase text-[10px]"
                >
                  {et.is_active ? "active" : "inactive"}
                </Badge>
              </div>
            </div>
            <HasAccess permission="write">
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("edit")}
                >
                  <Pencil className="h-4 w-4 mr-1" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    window.confirm("Delete this event type?") && remove.mutate()
                  }
                >
                  <Trash2 className="h-4 w-4 mr-1 text-red-500" /> Delete
                </Button>
              </div>
            </HasAccess>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Meta
                  label="Managed event"
                  value={
                    et.managed_event ? (
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        onClick={() =>
                          navigate(
                            `../../managed-events/${et.managed_event_id}`,
                          )
                        }
                      >
                        {et.managed_event.name}
                      </button>
                    ) : (
                      "—"
                    )
                  }
                />
                <Meta
                  label="Mapped to"
                  value={
                    et.professional
                      ? `Professional · ${et.professional.name}`
                      : et.teams?.length
                        ? `Team round-robin · ${et.teams.map((t) => t.name).join(", ")}`
                        : "—"
                  }
                />
                <Meta label="Type" value={et.type} />
                <Meta label="Active" value={et.is_active ? "Yes" : "No"} />
                <Meta
                  label="Calendly event type URI"
                  value={et.calendly_event_type_uri}
                />
                <Meta
                  label="Scheduling URL"
                  value={
                    et.scheduling_url ? (
                      <a
                        href={et.scheduling_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {et.scheduling_url}
                      </a>
                    ) : (
                      "—"
                    )
                  }
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Feature flags</CardTitle>
            </CardHeader>
            <CardContent>
              {!uri ? (
                <p className="text-sm text-gray-400">
                  This event type has no Calendly URI — flags are keyed by the
                  URI, so there's nothing to configure.
                </p>
              ) : (
                <div className="space-y-3">
                  {EVENT_TYPE_FLAGS.map((f) => {
                    const flag = flagByType(f.type);
                    const on = !!flag?.enabled;
                    return (
                      <div
                        key={f.type}
                        className="flex items-start justify-between gap-4 rounded-md border border-gray-200 p-3"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">{f.label}</p>
                            <Badge
                              variant="outline"
                              className="text-[10px] uppercase"
                            >
                              {flag ? (on ? "on" : "off") : "not set"}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {f.description}
                          </p>
                        </div>
                        <HasAccess permission="write">
                          <Switch
                            checked={on}
                            disabled={setFlag.isPending}
                            onCheckedChange={(v) =>
                              setFlag.mutate({ type: f.type, enabled: v })
                            }
                          />
                        </HasAccess>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </ContentLayout>
  );
}
